import { Bell, MessagesSquare } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../api/client';
import type { SupportPayload, SupportRequest, SupportSettings, StaffMe } from '../api/types';
import { SupportConversation } from '../components/SupportConversation';
import { SupportDesk } from '../components/SupportDesk';
import { ago } from '../lib/format';
import { outcome } from '../lib/support';
import { can, useMe } from '../lib/me';
import { assistNotificationState, requestAssistNotifications } from '../lib/useAssistPending';
import { useLoad } from '../lib/useLoad';
import { readParams, withParam, writeUrl } from '../lib/urlState';

/**
 * Operators who asked the help assistant for a person, and the conversation
 * with whoever claims them.
 *
 * TWO LEGS, AND THE SCREEN SAYS WHICH IS DOWN. The queue below is read straight
 * from sphere-crm's database, so it shows who is waiting even when the support
 * door is shut; opening and answering a conversation goes through the door. A
 * banner per leg, because they fail for different reasons and one banner would
 * hide the other.
 *
 * WHAT IS ON THE CARD, AND WHAT IS NOT. Who asked, from which tenant and
 * screen, what the conversation is about, and how long they have waited — the
 * queue is a list of requests addressed to staff, and anyone who can open
 * this screen may read it. The conversation itself is a customer's words:
 * opening it needs `support.respond` and leaves a row in the trail.
 */
const POLL_MS = 4000;

const STATUS_TONE: Record<string, string> = {
  waiting: 'badge-warn',
  with_staff: 'badge-ok',
};

function RequestCard({
  request,
  me,
  busy,
  onClaim,
  onOpen,
}: {
  request: SupportRequest;
  me: StaffMe | null;
  busy: boolean;
  onClaim: (request: SupportRequest, takeover: boolean) => void;
  onOpen: (request: SupportRequest) => void;
}) {
  const canAct = can(me, 'support.respond');
  const mine = request.status === 'with_staff' && request.claimedBy === me?.email;
  return (
    <div className={`card card-pad assist-card${request.urgency === 'urgent' ? ' support-card-urgent' : ''}`}>
      <div className="assist-card-head">
        <span className="badge badge-accent">{request.tenantKey}</span>
        <span className={`badge ${STATUS_TONE[request.status] ?? ''}`}>
          {request.status === 'with_staff' ? 'with staff' : request.status}
        </span>
        {request.urgency === 'urgent' && <span className="badge badge-danger">urgent</span>}
        <span className="assist-muted">asked {ago(request.waitingSince)}</span>
        <span className="wt-spacer" />
        {canAct && request.status === 'waiting' && (
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => onClaim(request, false)}>
            {busy ? 'Claiming…' : 'Claim'}
          </button>
        )}
        {canAct && mine && (
          <button type="button" className="btn btn-primary" onClick={() => onOpen(request)}>
            Open
          </button>
        )}
        {canAct && request.status === 'with_staff' && !mine && (
          <button type="button" className="btn btn-secondary" onClick={() => onOpen(request)}>
            Look
          </button>
        )}
      </div>
      <p className="assist-who">
        {request.operator.name || request.operator.email || 'An operator'}
        {request.operator.role && <span className="assist-muted"> · {request.operator.role}</span>}
        {request.screen && <span className="assist-muted"> · on {request.screen}</span>}
      </p>
      {request.topic && <p className="assist-note">“{request.topic}”</p>}
      {request.status === 'with_staff' && (
        <p className="assist-muted support-card-meta">
          With {mine ? 'you' : request.claimedBy} · {request.staffMessageCount} repl
          {request.staffMessageCount === 1 ? 'y' : 'ies'} · operator last checked in{' '}
          {request.operatorSeenAt ? ago(request.operatorSeenAt) : 'never'}
        </p>
      )}
    </div>
  );
}

export function Support() {
  const { data, error, loading, reload } = useLoad<SupportPayload>(
    () => api.get<SupportPayload>('/support'),
    [],
    POLL_MS,
  );
  // Who is looking decides what they are offered: the screen shows the queue,
  // support.respond can claim, support.hours can also set the desk's hours.
  const me = useMe();
  const settingsLoad = useLoad<SupportSettings>(() => api.get<SupportSettings>('/support/settings'), [], 60_000);
  const [savedSettings, setSavedSettings] = useState<SupportSettings | null>(null);

  // The open conversation is the URL's (?conversation=<id>): a reload, or a
  // link pasted to a colleague, opens the same one instead of the queue.
  const [openId, setOpenId] = useState<string | null>(() => readParams().get('conversation'));
  useEffect(() => {
    writeUrl(withParam(readParams(), 'conversation', openId));
    // The conversation replaces the queue in place, so without this it opens
    // wherever the queue was scrolled to -- on a phone, halfway down the thread.
    window.scrollTo(0, 0);
  }, [openId]);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notifyState, setNotifyState] = useState(assistNotificationState);

  const claim = async (request: SupportRequest, takeover: boolean) => {
    setBusy(request.id);
    setActionError(null);
    try {
      await api.post(`/support/${request.id}/claim`, takeover ? { takeover: true } : {});
      setOpenId(request.id);
    } catch (caught) {
      // "Priya has this conversation" — the queue refreshes underneath.
      setActionError(caught instanceof Error ? caught.message : String(caught));
      void reload();
    } finally {
      setBusy(null);
    }
  };

  if (openId) {
    return (
      <SupportConversation
        id={openId}
        me={me}
        onBack={() => {
          setOpenId(null);
          void reload();
        }}
      />
    );
  }

  const canAct = can(me, 'support.respond');
  const settings = savedSettings ?? settingsLoad.data;

  return (
    <div className="page">
      <div className="page-head">
        <h1 className="page-title">
          <MessagesSquare size={18} /> Support
        </h1>
        <p className="page-sub">
          Operators who asked the help assistant for a person. Claim one to answer them in their own conversation —
          they see “LimoSphere Support”, not your name.
        </p>
      </div>

      {notifyState === 'default' && (
        <p className="assist-alerts">
          <Bell size={14} />
          Requests arrive while nobody is on this screen.
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => void requestAssistNotifications().then(setNotifyState)}
          >
            Notify me
          </button>
        </p>
      )}

      <SupportDesk
        settings={settings}
        error={settingsLoad.error && !settings ? settingsLoad.error.message : null}
        canEdit={can(me, 'support.hours')}
        onSaved={setSavedSettings}
      />

      {error && <p className="note note-danger">Could not read the queue: {error.message}</p>}
      {actionError && <p className="note note-danger">{actionError}</p>}
      {data && !data.sql.available && <p className="note note-warn">The queue cannot be read: {data.sql.reason}</p>}
      {data && !data.door.configured && (
        <p className="note note-warn">
          The support bridge is off — {data.door.reason}. Waiting operators are listed, but nobody can open or answer
          a conversation from here until it is set.
        </p>
      )}
      {data && !canAct && me && (
        <p className="note">
          You can see who is waiting. Opening and answering a conversation needs a permission you do not have.
        </p>
      )}

      {loading && !data && <p className="note">Loading…</p>}

      {data?.sql.available && data.open.length === 0 && (
        <div className="card card-pad assist-empty">
          <MessagesSquare size={22} />
          <p>Nobody is waiting for a person.</p>
        </div>
      )}

      {data?.open.map((request) => (
        <RequestCard
          key={request.id}
          request={request}
          me={me}
          busy={busy === request.id}
          onClaim={(target, takeover) => void claim(target, takeover)}
          onOpen={(target) => setOpenId(target.id)}
        />
      ))}

      {data && data.recent.length > 0 && (
        <>
          <h2 className="assist-subhead">Recent — the last 7 days</h2>
          <div className="card wt-table-wrap">
            <table className="assist-table wt-stack">
              <thead>
                <tr>
                  <th>Tenant</th>
                  <th>Who</th>
                  <th>About</th>
                  <th>Outcome</th>
                  <th>Handled by</th>
                  <th>Closed</th>
                </tr>
              </thead>
              <tbody>
                {data.recent.map((request) => (
                  <tr key={request.id} className={canAct ? 'support-row-open' : ''}>
                    <td data-label="Tenant">{request.tenantKey}</td>
                    <td data-label="Who">{request.operator.name || request.operator.email || '—'}</td>
                    <td className="support-topic-cell wt-stack-wide" data-label="About">
                      {canAct ? (
                        <button type="button" className="support-link" onClick={() => setOpenId(request.id)}>
                          {request.topic || 'Open'}
                        </button>
                      ) : (
                        request.topic || '—'
                      )}
                    </td>
                    <td data-label="Outcome">{outcome(request)}</td>
                    <td className="wt-stack-wide" data-label="Handled by">{request.claimedBy || '—'}</td>
                    <td className="assist-muted" data-label="Closed">
                      {ago(request.closedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
